const CACHE_PREFIX = 'muzone.profilePicture.';
export const DEFAULT_AVATAR_CACHE_TTL_MS = 60 * 60 * 1000; // 1 hour

const getSessionStorage = () => {
    if (typeof window === 'undefined') {
        return null;
    }
    try {
        return window.sessionStorage || null;
    } catch {
        return null;
    }
};

const normalizeVersion = (value) => {
    // Ensure cache-busting stays numeric and deterministic across reads/writes.
    const numeric = Number(value);
    if (!Number.isFinite(numeric) || numeric < 0) {
        return 0;
    }
    return numeric;
};

const getCacheKey = (userId) => {
    if (userId === null || userId === undefined) {
        return null;
    }
    const key = String(userId);
    if (!key) {
        return null;
    }
    return `${CACHE_PREFIX}${key}`;
};

export const readAvatarCache = (userId) => {
    const sessionStorage = getSessionStorage();
    if (!sessionStorage) {
        return null;
    }
    const key = getCacheKey(userId);
    if (!key) {
        return null;
    }
    try {
        const raw = sessionStorage.getItem(key);
        if (!raw) {
            return null;
        }
        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== 'object') {
            return null;
        }
        return {
            url: typeof parsed.url === 'string' ? parsed.url : null,
            cachedAt: typeof parsed.cachedAt === 'number' ? parsed.cachedAt : 0,
            version: normalizeVersion(parsed.version),
        };
    } catch (error) {
        console.warn('Failed to read avatar cache entry', error);
        return null;
    }
};

export const writeAvatarCache = (userId, url, version = 0) => {
    const sessionStorage = getSessionStorage();
    if (!sessionStorage) {
        return;
    }
    const key = getCacheKey(userId);
    if (!key) {
        return;
    }
    if (!url) {
        sessionStorage.removeItem(key);
        return;
    }
    try {
        const entry = {
            url,
            cachedAt: Date.now(),
            // Always persist a numeric version for deterministic cache-busting.
            version: normalizeVersion(version),
        };
        sessionStorage.setItem(
            key,
            JSON.stringify(entry)
        );
    } catch (error) {
        console.warn('Failed to persist avatar cache entry', error);
    }
};

export const clearAvatarCache = (userId) => {
    const sessionStorage = getSessionStorage();
    if (!sessionStorage) {
        return;
    }
    const key = getCacheKey(userId);
    if (!key) {
        return;
    }
    try {
        sessionStorage.removeItem(key);
    } catch (error) {
        console.warn('Failed to clear avatar cache entry', error);
    }
};

export const isAvatarCacheFresh = (entry, ttlMs = DEFAULT_AVATAR_CACHE_TTL_MS) => {
    if (!entry || !entry.cachedAt) {
        return false;
    }
    return Date.now() - entry.cachedAt < ttlMs;
};

/**
 * Appends a query parameter to a URL, handling existing query strings correctly.
 * @param {string} url - The base URL
 * @param {string} key - The query parameter key
 * @param {string|number} value - The query parameter value
 * @returns {string} The URL with the query parameter appended
 */
export const appendQueryParam = (url, key, value) => {
    if (!url || !key) {
        return url;
    }
    const hashIndex = url.indexOf('#');
    const hash = hashIndex >= 0 ? url.slice(hashIndex) : '';
    const baseWithQuery = hashIndex >= 0 ? url.slice(0, hashIndex) : url;
    const queryIndex = baseWithQuery.indexOf('?');
    const base = queryIndex >= 0 ? baseWithQuery.slice(0, queryIndex) : baseWithQuery;
    const queryString = queryIndex >= 0 ? baseWithQuery.slice(queryIndex + 1) : '';
    const params = new URLSearchParams(queryString);
    // Replace existing params to avoid duplicate keys (e.g., v=1&v=2).
    params.set(key, String(value));
    const nextQuery = Array.from(params.entries())
        .map(([entryKey, entryValue]) => (
            `${encodeURIComponent(entryKey)}=${encodeURIComponent(entryValue)}`
        ))
        .join('&');
    if (!nextQuery) {
        return `${base}${hash}`;
    }
    return `${base}?${nextQuery}${hash}`;
};

/**
 * Warms the browser image cache by preloading an image.
 * Uses versioned URL if version is provided for proper cache-busting.
 * @param {string} url - The image URL
 * @param {number} version - Version number for cache-busting
 */
export const warmAvatarImageCache = (url, version = 0) => {
    if (typeof window === 'undefined' || !url) {
        return;
    }
    try {
        const normalizedVersion = normalizeVersion(version);
        const finalUrl = normalizedVersion > 0
            ? appendQueryParam(url, 'v', normalizedVersion)
            : url;
        const img = new Image();
        img.onerror = () => {
            // Silently handle image load errors (e.g., 404, network issues)
            // Don't log to avoid noise in console
        };
        img.src = finalUrl;
    } catch (error) {
        // Silently handle any errors during cache warming
        // Don't log to avoid noise in console
    }
};
